import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import {
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { useCallback, useEffect } from 'react';

export type SerializedImageNode = Spread<{ src: string; alt: string }, SerializedLexicalNode>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __alt: string;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__alt, node.__key);
  }

  static importJSON(serialized: SerializedImageNode): ImageNode {
    return $createImageNode(serialized.src, serialized.alt);
  }

  constructor(src: string, alt: string = '', key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__alt = alt;
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      type: 'image',
      version: 1,
      src: this.__src,
      alt: this.__alt,
    };
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    span.style.display = 'block';
    return span;
  }

  updateDOM(): false {
    // Returning false tells Lexical to never patch the DOM element
    return false;
  }

  isInline(): false {
    return false;
  }

  decorate(): JSX.Element {
    return <ImageComponent src={this.__src} alt={this.__alt} nodeKey={this.__key} />;
  }

  getSrc(): string {
    return this.__src;
  }

  getAlt(): string {
    return this.__alt;
  }

  // Node -> HTML: called by $generateHtmlFromNodes for each node
  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const img = document.createElement('img');
    img.setAttribute('src', this.__src);
    img.setAttribute('alt', this.__alt);
    return { element: img };
  }

  // HTML -> Node: called by $generateNodesFromDOM for each DOM element
  static importDOM(): DOMConversionMap {
    return {
      // Key is the HTML tag name to handle
      img: () => ({
        conversion: (domNode: HTMLElement): DOMConversionOutput | null => {
          if (!(domNode instanceof HTMLImageElement)) {
            return null;
          }

          const src = domNode.getAttribute('src');
          const alt = domNode.getAttribute('alt') ?? '';

          if (!src) {
            return null;
          }

          return {
            node: $createImageNode(src, alt),
          };
        },
        priority: 0, // higher priority overrides default Lexical handling
      }),
    };
  }
}

function ImageComponent({ src, alt, nodeKey }: { src: string; alt: string; nodeKey: NodeKey }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      // Only handle clicks that land on this image's DOM node
      const imageEl = editor.getElementByKey(nodeKey);
      if (!imageEl?.contains(e.target as Node)) {
        return false;
      }

      clearSelection();
      setSelected(true);
      return true;
    },
    [editor, nodeKey, setSelected, clearSelection]
  );

  useEffect(() => {
    return editor.registerCommand(CLICK_COMMAND, handleClick, COMMAND_PRIORITY_LOW);
  }, [editor, handleClick]);

  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      style={{
        display: 'block',
        maxWidth: '90%',
        maxHeight: '200px',
        outline: isSelected ? '2px solid #3182ce' : 'none',
        outlineOffset: '2px',
        borderRadius: '2px',
      }}
    />
  );
}

export function $createImageNode(src: string, alt = ''): ImageNode {
  return new ImageNode(src, alt);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
